import { useQuery } from "@tanstack/react-query";

import { api } from "../api/client";
import type { SymbolMeta } from "../api/types";

interface Props {
  yahoo: string;
  /** Last close from indicators — used to compute % upside/downside vs analyst PT. */
  lastClose: number | null;
}

function fmtCurrency(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  return Number(v).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function fmtBigCurrency(v: number | null | undefined): string {
  if (v === null || v === undefined) return "—";
  if (v >= 1e12) return `$${(v / 1e12).toFixed(2)}T`;
  if (v >= 1e9) return `$${(v / 1e9).toFixed(2)}B`;
  if (v >= 1e6) return `$${(v / 1e6).toFixed(2)}M`;
  return `$${v.toLocaleString()}`;
}

function recColor(rec: string | null): string {
  if (!rec) return "text-slate-400";
  const r = rec.toLowerCase();
  if (r.includes("strong_buy") || r === "buy") return "text-emerald-300";
  if (r === "hold" || r === "neutral") return "text-slate-300";
  if (r.includes("sell")) return "text-rose-300";
  return "text-slate-400";
}

function recLabel(rec: string | null): string {
  if (!rec) return "—";
  // yfinance values: "strong_buy", "buy", "hold", "sell", "underperform", "none"
  return rec.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

export default function FundamentalsCard({ yahoo, lastClose }: Props) {
  const meta = useQuery<SymbolMeta>({
    queryKey: ["symbol-meta", yahoo],
    queryFn: async () => (await api.get<SymbolMeta>(`/symbols/${encodeURIComponent(yahoo)}`)).data,
    enabled: !!yahoo,
    staleTime: 60_000,
  });

  if (meta.isLoading || !meta.data) return null;
  const m = meta.data;

  const upsidePct =
    m.analyst_target_mean !== null && lastClose !== null && lastClose > 0
      ? (m.analyst_target_mean - lastClose) / lastClose * 100
      : null;
  const upsideCls =
    upsidePct === null
      ? "text-slate-400"
      : upsidePct >= 0
      ? "text-emerald-300"
      : "text-rose-300";

  // Hide the whole card if there's literally no data to show.
  const anyDividend = m.dividend_yield !== null || m.dividend_rate !== null || m.ex_dividend_date !== null;
  const anyAnalyst = m.analyst_target_mean !== null || m.analyst_recommendation !== null;
  const anyMeta = m.market_cap !== null || m.sector !== null;
  if (!anyDividend && !anyAnalyst && !anyMeta) return null;

  return (
    <section className="rounded-2xl border border-slate-800 bg-slate-900 p-4">
      <div className="mb-3 flex items-baseline justify-between">
        <div className="text-sm font-medium text-slate-300">Fundamentals</div>
        <div className="text-[11px] text-slate-500">yfinance · cached nightly</div>
      </div>

      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        {/* Sector / industry */}
        {(m.sector || m.industry) && (
          <div className="col-span-2 sm:col-span-2">
            <dt className="text-[10px] uppercase tracking-wide text-slate-500">Sector</dt>
            <dd className="text-sm text-slate-200">
              {m.sector ?? "—"}
              {m.industry && (
                <span className="text-slate-500"> · {m.industry}</span>
              )}
            </dd>
          </div>
        )}

        {m.market_cap !== null && (
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-slate-500">Market cap</dt>
            <dd className="text-sm tabular-nums text-slate-200">{fmtBigCurrency(m.market_cap)}</dd>
          </div>
        )}

        {m.next_earnings_date && (
          <div>
            <dt className="text-[10px] uppercase tracking-wide text-slate-500">Earnings</dt>
            <dd className="text-sm tabular-nums text-slate-200">{m.next_earnings_date}</dd>
          </div>
        )}

        {/* Analyst block */}
        {anyAnalyst && (
          <>
            <div>
              <dt className="text-[10px] uppercase tracking-wide text-slate-500">
                Analyst PT (avg)
              </dt>
              <dd className="text-sm tabular-nums text-slate-200">
                {m.analyst_target_mean === null ? (
                  "—"
                ) : (
                  <>
                    {fmtCurrency(m.analyst_target_mean)}
                    {upsidePct !== null && (
                      <span className={`ml-1.5 text-xs ${upsideCls}`}>
                        ({upsidePct >= 0 ? "+" : ""}{upsidePct.toFixed(1)}%)
                      </span>
                    )}
                  </>
                )}
              </dd>
              {m.analyst_target_high !== null && m.analyst_target_low !== null && (
                <dd className="mt-0.5 text-[11px] tabular-nums text-slate-500">
                  range {fmtCurrency(m.analyst_target_low)}–{fmtCurrency(m.analyst_target_high)}
                  {m.analyst_count !== null && <> · {m.analyst_count} analysts</>}
                </dd>
              )}
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-wide text-slate-500">Consensus</dt>
              <dd className={`text-sm font-medium ${recColor(m.analyst_recommendation)}`}>
                {recLabel(m.analyst_recommendation)}
              </dd>
            </div>
          </>
        )}

        {/* Dividend block */}
        {anyDividend && (
          <>
            <div>
              <dt className="text-[10px] uppercase tracking-wide text-slate-500">Dividend yield</dt>
              <dd className="text-sm tabular-nums text-amber-300">
                {m.dividend_yield === null
                  ? "—"
                  : `${(m.dividend_yield * 100).toFixed(2)}%`}
              </dd>
              {m.dividend_rate !== null && (
                <dd className="mt-0.5 text-[11px] tabular-nums text-slate-500">
                  {fmtCurrency(m.dividend_rate)} {m.currency}/share annual
                </dd>
              )}
            </div>
            <div>
              <dt className="text-[10px] uppercase tracking-wide text-slate-500">Ex-dividend</dt>
              <dd className="text-sm tabular-nums text-slate-200">
                {m.ex_dividend_date ?? "—"}
              </dd>
            </div>
          </>
        )}
      </dl>
    </section>
  );
}
